import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import IdentityAssistPanel from "../components/IdentityAssistPanel";

interface Server {
  dns_name: string;
  ip_address?: string;
}
interface Scope {
  scope_id: string;
  name?: string;
  subnet_mask?: string;
  start_range?: string;
  end_range?: string;
  state?: string;
  lease_duration?: string;
  in_use?: number | null;
  free?: number | null;
  pct_in_use?: number | null;
}
interface Lease {
  ip_address: string;
  client_id?: string;
  host_name?: string;
  state?: string;
  expiry?: string;
}
interface Reservation {
  ip_address: string;
  client_id?: string;
  name?: string;
  description?: string;
}
interface Opt {
  option_id: number;
  name?: string;
  type?: string;
  value: string[];
}
interface Excl {
  start: string;
  end: string;
}

const CARD =
  "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const INPUT =
  "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm";

function errText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: string } } };
  return err.response?.data?.detail || fallback;
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function ToolsDhcp() {
  const { currentCustomerId, current } = useCustomer();
  const [servers, setServers] = useState<Server[] | null>(null);
  const [server, setServer] = useState("");
  const [scopes, setScopes] = useState<Scope[] | null>(null);
  const [scopeId, setScopeId] = useState("");
  const [leases, setLeases] = useState<Lease[] | null>(null);
  const [reservations, setReservations] = useState<Reservation[] | null>(null);
  const [options, setOptions] = useState<Opt[] | null>(null);
  const [exclusions, setExclusions] = useState<Excl[] | null>(null);
  const [tab, setTab] = useState<"reservations" | "leases" | "options" | "exclusions">("reservations");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // forms
  const [rIp, setRIp] = useState("");
  const [rMac, setRMac] = useState("");
  const [rName, setRName] = useState("");
  const [exStart, setExStart] = useState("");
  const [exEnd, setExEnd] = useState("");
  const [optId, setOptId] = useState("6");
  const [optVal, setOptVal] = useState("");
  /* Server-level ("master") options. A value set here applies to EVERY scope
     that does not override it, which is how an operator sets DNS once
     instead of on fourteen scopes. The screen could only address a scope, so
     these were invisible - and a scope inheriting its DNS from the server
     showed no DNS option at all, which reads as "not configured". */
  const [srvOptions, setSrvOptions] = useState<Opt[] | null>(null);
  const [showSrvOptions, setShowSrvOptions] = useState(false);
  const [srvOptId, setSrvOptId] = useState("6");
  const [srvOptVal, setSrvOptVal] = useState("");
  const [showNewScope, setShowNewScope] = useState(false);
  const [ns, setNs] = useState({ name: "", start: "", end: "", mask: "255.255.255.0", gw: "", dns: "", days: 8 });

  const leaseSort = useSort("ip_address");
  const resSort = useSort("ip_address");
  const scopeSort = useSort("scope_id");
  const optSort = useSort("option_id");
  const exclSort = useSort("start");

  const runCommand = useCallback(
    async (start: () => Promise<{ data: { command_id: number } }>): Promise<any> => {
      const cid = currentCustomerId;
      const r = await start();
      const id = r.data.command_id;
      const deadline = Date.now() + 60000;
      while (Date.now() < deadline) {
        await sleep(2500);
        const p = await api.get(`/api/v1/dhcp/${cid}/commands/${id}`);
        if (p.data.status === "completed") return p.data.result || {};
        if (p.data.status === "failed" || p.data.status === "expired")
          throw new Error(p.data.error_message || `Command ${p.data.status}`);
      }
      throw new Error("Timed out waiting for the AD runner (is it online?).");
    },
    [currentCustomerId],
  );

  const loadServers = useCallback(async () => {
    if (currentCustomerId == null) return;
    setBusy("servers");
    setError("");
    setServers(null);
    setServer("");
    setScopes(null);
    setScopeId("");
    try {
      const res = await runCommand(() => api.post(`/api/v1/dhcp/${currentCustomerId}/servers/refresh`));
      setServers(res.servers || []);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't load DHCP servers"));
    } finally {
      setBusy("");
    }
  }, [currentCustomerId, runCommand]);

  const loadScopes = async (srv: string) => {
    setBusy("scopes");
    setError("");
    setServer(srv);
    setScopes(null);
    setScopeId("");
    try {
      const res = await runCommand(() =>
        api.post(`/api/v1/dhcp/${currentCustomerId}/scopes/refresh`, { server: srv }),
      );
      setScopes(res.scopes || []);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't load scopes"));
    } finally {
      setBusy("");
    }
  };

  const openScope = async (sid: string) => {
    setScopeId(sid);
    setTab("reservations");
    setLeases(null);
    setReservations(null);
    setOptions(null);
    setExclusions(null);
    await refresh("reservations", sid);
    refresh("leases", sid);
  };

  // Load the data for a tab (queued+polled). server/scopeId from args or state.
  const refresh = async (which: string, sid = scopeId) => {
    const map: Record<string, [string, (r: any) => void]> = {
      reservations: ["reservations", (r) => setReservations(r.reservations || [])],
      leases: ["leases", (r) => setLeases(r.leases || [])],
      options: ["options", (r) => setOptions(r.options || [])],
      exclusions: ["exclusions", (r) => setExclusions(r.exclusions || [])],
    };
    const [ep, setter] = map[which];
    setBusy(which);
    setError("");
    try {
      const res = await runCommand(() =>
        api.post(`/api/v1/dhcp/${currentCustomerId}/${ep}/refresh`, { server, scope_id: sid }),
      );
      setter(res);
    } catch (e) {
      if (which !== "leases") setError(errText(e, (e as Error).message || `Couldn't load ${which}`));
    } finally {
      setBusy("");
    }
  };

  /** Server-level options. scope_id is deliberately omitted, not blank-passed. */
  const refreshServerOptions = async () => {
    setBusy("srvoptions");
    setError("");
    try {
      const res = await runCommand(() =>
        api.post(`/api/v1/dhcp/${currentCustomerId}/options/refresh`, { server }),
      );
      setSrvOptions(res.options || []);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't load server options"));
    } finally {
      setBusy("");
    }
  };

  const openServerOptions = () => {
    const next = !showSrvOptions;
    setShowSrvOptions(next);
    if (next && srvOptions === null) refreshServerOptions();
  };

  const deleteScope = async (sid: string) => {
    const sel = (scopes || []).find((s) => s.scope_id === sid);
    // Typed confirmation, not an OK button: this stops the server answering
    // for a whole subnet and takes every lease and reservation with it.
    const typed = window.prompt(
      `Delete scope ${sid}${sel?.name ? ` (${sel.name})` : ""}?\n\n` +
        `The server stops handing out addresses on that subnet and every ` +
        `lease, reservation and exclusion in the scope is removed. Machines ` +
        `keep their current address until it expires, then get nothing.\n\n` +
        `Type the scope id to confirm:`,
    );
    if (typed?.trim() !== sid) return;
    await post("scopes/delete", { server, scope_id: sid, confirm: true }, () => {
      setScopeId("");
      loadScopes(server);
    });
  };

  const openTab = (t: typeof tab) => {
    setTab(t);
    if (t === "options" && options === null) refresh("options");
    if (t === "exclusions" && exclusions === null) refresh("exclusions");
  };

  const post = async (path: string, body: object, after: () => void) => {
    setBusy("act");
    setError("");
    try {
      await runCommand(() => api.post(`/api/v1/dhcp/${currentCustomerId}/${path}`, body));
      after();
    } catch (e) {
      setError(errText(e, (e as Error).message || "Action failed"));
      setBusy("");
    }
  };

  useEffect(() => {
    setServers(null);
    setServer("");
    setScopes(null);
    setScopeId("");
    setError("");
  }, [currentCustomerId]);

  if (currentCustomerId == null) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">DHCP</h1>
        <p className="text-sm text-gray-400 dark:text-gray-500 italic mt-4">
          Select a customer in the header to manage its DHCP.
        </p>
      </div>
    );
  }

  const sortedLeases = sortRows(leases || [], leaseSort.sort, (l, k) =>
    String((l as any)[k] ?? "").toLowerCase(),
  );
  const sortedRes = sortRows(reservations || [], resSort.sort, (r, k) =>
    String((r as any)[k] ?? "").toLowerCase(),
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">DHCP</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            AD-authorized DHCP servers for {current?.name}, via its AD runner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {server && scopes && (
            <button
              onClick={() => setShowNewScope((v) => !v)}
              disabled={!!busy}
              className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              + New scope
            </button>
          )}
          <button
            onClick={loadServers}
            disabled={!!busy}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === "servers" ? "Loading…" : servers ? "Reload servers" : "Load servers"}
          </button>
        </div>
      </div>

      <IdentityAssistPanel customerId={currentCustomerId} scope="dhcp" />

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {error}
        </p>
      )}
      {busy && !error && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <span className="h-4 w-4 shrink-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
          <div>
            <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Working…</div>
            <div className="text-xs text-blue-700 dark:text-blue-300">
              Waiting for {current?.name}'s on-site runner — usually a few seconds, up to ~20s.
            </div>
          </div>
        </div>
      )}

      {showNewScope && server && (
        <div className={`${CARD} p-4 mb-3`}>
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
            New scope on {server}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <input value={ns.name} onChange={(e) => setNs({ ...ns, name: e.target.value })} placeholder="Name" className={`${INPUT} w-40`} />
            <input value={ns.start} onChange={(e) => setNs({ ...ns, start: e.target.value })} placeholder="Start IP" className={`${INPUT} w-32`} />
            <input value={ns.end} onChange={(e) => setNs({ ...ns, end: e.target.value })} placeholder="End IP" className={`${INPUT} w-32`} />
            <input value={ns.mask} onChange={(e) => setNs({ ...ns, mask: e.target.value })} placeholder="Subnet mask" className={`${INPUT} w-32`} />
            <input value={ns.gw} onChange={(e) => setNs({ ...ns, gw: e.target.value })} placeholder="Gateway (opt)" className={`${INPUT} w-32`} />
            <input value={ns.dns} onChange={(e) => setNs({ ...ns, dns: e.target.value })} placeholder="DNS (comma-sep, opt)" className={`${INPUT} w-40`} />
            <input type="number" value={ns.days} onChange={(e) => setNs({ ...ns, days: Number(e.target.value) || 8 })} title="Lease days" className={`${INPUT} w-20`} />
            <button
              onClick={() =>
                post(
                  "scopes",
                  {
                    server,
                    name: ns.name,
                    start_range: ns.start,
                    end_range: ns.end,
                    subnet_mask: ns.mask,
                    gateway: ns.gw,
                    dns_servers: ns.dns.split(",").map((s) => s.trim()).filter(Boolean),
                    lease_days: ns.days,
                  },
                  () => {
                    setShowNewScope(false);
                    loadScopes(server);
                  },
                )
              }
              disabled={!!busy || !ns.name || !ns.start || !ns.end || !ns.mask}
              className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Create scope
            </button>
          </div>
        </div>
      )}

      {servers && servers.length === 0 && (
        <div className={`${CARD} p-6 text-sm text-gray-500 dark:text-gray-400`}>
          No DHCP servers available. (No DHCP servers are authorized in this customer's Active
          Directory.)
        </div>
      )}

      {servers && servers.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {servers.map((s) => (
              <button
                key={s.dns_name}
                onClick={() => loadScopes(s.dns_name)}
                className={`text-sm px-3 py-1.5 rounded-lg border ${
                  server === s.dns_name
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                }`}
                title={s.ip_address}
              >
                {s.dns_name}
              </button>
            ))}
          </div>

          {server && (
            <div className={`${CARD} p-4`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    Server options{" "}
                    <span className="font-normal text-gray-500 dark:text-gray-400">— {server}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Set once here and every scope inherits it unless that scope sets its own.
                  </p>
                </div>
                <button
                  onClick={openServerOptions}
                  disabled={!!busy}
                  className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {showSrvOptions ? "Hide" : busy === "srvoptions" ? "Loading…" : "Show"}
                </button>
              </div>

              {showSrvOptions && (
                <div className="mt-3">
                  <div className="flex flex-wrap items-end gap-2 mb-3">
                    <select value={srvOptId} onChange={(e) => setSrvOptId(e.target.value)} className={INPUT} title="Option">
                      <option value="6">6 — DNS servers</option>
                      <option value="3">3 — Router / gateway</option>
                      <option value="15">15 — DNS domain</option>
                      <option value="44">44 — WINS servers</option>
                      <option value="42">42 — NTP servers</option>
                    </select>
                    <input
                      value={srvOptVal}
                      onChange={(e) => setSrvOptVal(e.target.value)}
                      placeholder="value(s), comma-separated"
                      className={`${INPUT} w-64`}
                    />
                    <button
                      onClick={() =>
                        post(
                          "options",
                          {
                            server,
                            option_id: Number(srvOptId),
                            values: srvOptVal.split(",").map((v) => v.trim()).filter(Boolean),
                          },
                          () => {
                            setSrvOptVal("");
                            refreshServerOptions();
                          },
                        )
                      }
                      disabled={!!busy || !srvOptVal.trim()}
                      title="Applies to every scope on this server that does not set its own value"
                      className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Set for all scopes
                    </button>
                  </div>
                  <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left">Option</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Value</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {srvOptions === null && (
                          <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                        )}
                        {(srvOptions || []).map((o, i) => (
                          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                            <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{o.option_id}</td>
                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{o.name}</td>
                            <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300 break-all">{(o.value || []).join(", ")}</td>
                            <td className="px-3 py-1.5 text-right">
                              <button
                                onClick={() => {
                                  if (
                                    !window.confirm(
                                      `Remove server option ${o.option_id} (${o.name})?\n\n` +
                                        `Scopes that were inheriting this value will have nothing ` +
                                        `to inherit. Scopes that set their own are unaffected.`,
                                    )
                                  )
                                    return;
                                  post("options/delete", { server, option_id: o.option_id }, () =>
                                    refreshServerOptions(),
                                  );
                                }}
                                disabled={!!busy}
                                className="text-xs border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 px-2.5 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                        {srvOptions && srvOptions.length === 0 && (
                          <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No server-level options set — every scope carries its own.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {scopes && (
            <div className={`${CARD} overflow-x-auto`}>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                  <tr>
                    <SortableTh label="Scope" sortKey="scope_id" sort={scopeSort.sort} onSort={scopeSort.toggle} />
                    <SortableTh label="Name" sortKey="name" sort={scopeSort.sort} onSort={scopeSort.toggle} />
                    <th className="px-3 py-2 text-left">Range</th>
                    <SortableTh label="State" sortKey="state" sort={scopeSort.sort} onSort={scopeSort.toggle} />
                    <SortableTh label="Utilization" sortKey="pct_in_use" sort={scopeSort.sort} onSort={scopeSort.toggle} align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {scopes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">
                        No scopes on this server.
                      </td>
                    </tr>
                  )}
                  {sortRows(scopes, scopeSort.sort, (sc, key) => {
                    switch (key) {
                      case "name":
                        return (sc.name || "").toLowerCase();
                      case "state":
                        return (sc.state || "").toLowerCase();
                      case "pct_in_use":
                        return sc.pct_in_use ?? 0;
                      default:
                        return (sc.scope_id || "").toLowerCase();
                    }
                  }).map((sc) => (
                    <tr
                      key={sc.scope_id}
                      onClick={() => openScope(sc.scope_id)}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                        scopeId === sc.scope_id ? "bg-blue-50 dark:bg-blue-900/20" : ""
                      }`}
                    >
                      <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-gray-100">{sc.scope_id}</td>
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{sc.name}</td>
                      <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                        {sc.start_range} – {sc.end_range}
                      </td>
                      <td className="px-3 py-1.5">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            sc.state === "Active"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {sc.state}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-gray-600 dark:text-gray-300">
                        {sc.pct_in_use != null ? `${sc.pct_in_use}%` : "—"}
                        {sc.in_use != null && sc.free != null && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                            ({sc.in_use}/{sc.in_use + sc.free})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {scopeId &&
            (() => {
              const sel = (scopes || []).find((s) => s.scope_id === scopeId);
              return (
                <div className={`${CARD} p-4`}>
                  {/* Scope header + edit */}
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      Scope {scopeId} {sel?.name && <span className="text-gray-500 dark:text-gray-400">— {sel.name}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          post(
                            "scopes/update",
                            { server, scope_id: scopeId, state: sel?.state === "Active" ? "Inactive" : "Active" },
                            () => loadScopes(server),
                          )
                        }
                        disabled={!!busy}
                        className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      >
                        {sel?.state === "Active" ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        onClick={() => deleteScope(scopeId)}
                        disabled={!!busy}
                        title="Removes the scope and everything in it — the server stops answering for this subnet"
                        className="text-xs border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 px-2.5 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                      >
                        Delete scope
                      </button>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex items-center gap-1 mb-3 border-b border-gray-100 dark:border-gray-700">
                    {(["reservations", "leases", "options", "exclusions"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => openTab(t)}
                        className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px capitalize ${
                          tab === t
                            ? "border-blue-600 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  {tab === "reservations" && (
                    <>
                      <div className="flex flex-wrap items-end gap-2 mb-3">
                        <input value={rIp} onChange={(e) => setRIp(e.target.value)} placeholder="IP" className={`${INPUT} w-32`} />
                        <input value={rMac} onChange={(e) => setRMac(e.target.value)} placeholder="MAC" className={`${INPUT} w-44`} />
                        <input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="name (opt)" className={`${INPUT} w-36`} />
                        <button
                          onClick={() =>
                            post("reservations", { server, scope_id: scopeId, ip: rIp, mac: rMac, name: rName }, () => {
                              setRIp(""); setRMac(""); setRName(""); refresh("reservations");
                            })
                          }
                          disabled={!!busy || !rIp.trim() || !rMac.trim()}
                          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Add reservation
                        </button>
                      </div>
                      <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-lg">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                            <tr>
                              <SortableTh label="IP" sortKey="ip_address" sort={resSort.sort} onSort={resSort.toggle} />
                              <SortableTh label="MAC" sortKey="client_id" sort={resSort.sort} onSort={resSort.toggle} />
                              <SortableTh label="Name" sortKey="name" sort={resSort.sort} onSort={resSort.toggle} />
                              <th></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {reservations === null && (
                              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                            )}
                            {sortedRes.map((r, i) => (
                              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{r.ip_address}</td>
                                <td className="px-3 py-1.5 font-mono text-xs text-gray-600 dark:text-gray-300">{r.client_id}</td>
                                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{r.name}</td>
                                <td className="px-3 py-1.5 text-right">
                                  <button
                                    onClick={() => window.confirm(`Delete reservation ${r.ip_address}?`) && post("reservations/delete", { server, scope_id: scopeId, ip: r.ip_address }, () => refresh("reservations"))}
                                    disabled={!!busy}
                                    className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {reservations && reservations.length === 0 && (
                              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No reservations.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {tab === "leases" && (
                    <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                          <tr>
                            <SortableTh label="IP" sortKey="ip_address" sort={leaseSort.sort} onSort={leaseSort.toggle} />
                            <SortableTh label="MAC" sortKey="client_id" sort={leaseSort.sort} onSort={leaseSort.toggle} />
                            <SortableTh label="Host" sortKey="host_name" sort={leaseSort.sort} onSort={leaseSort.toggle} />
                            <SortableTh label="State" sortKey="state" sort={leaseSort.sort} onSort={leaseSort.toggle} />
                            <th></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {leases === null && (
                            <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                          )}
                          {sortedLeases.map((l, i) => (
                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                              <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{l.ip_address}</td>
                              <td className="px-3 py-1.5 font-mono text-xs text-gray-600 dark:text-gray-300">{l.client_id}</td>
                              <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{l.host_name}</td>
                              <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{l.state}</td>
                              <td className="px-3 py-1.5 text-right">
                                <button
                                  onClick={() => post("reservations", { server, scope_id: scopeId, ip: l.ip_address, mac: l.client_id || "", name: l.host_name || "" }, () => { setTab("reservations"); refresh("reservations"); })}
                                  disabled={!!busy || !l.client_id}
                                  title="Convert this lease to a reservation"
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                                >
                                  Reserve
                                </button>
                                {/* Deleting a lease was the one DHCP object
                                    that could only be looked at. Freeing the
                                    address a decommissioned machine still
                                    holds is routine, and without this it
                                    meant opening the DHCP console. */}
                                <button
                                  onClick={() =>
                                    window.confirm(
                                      `Delete the lease for ${l.ip_address}` +
                                        `${l.host_name ? ` (${l.host_name})` : ""}?\n\n` +
                                        "The address returns to the pool. If the device is " +
                                        "still on the network it will simply ask for one " +
                                        "again — possibly the same one.",
                                    ) &&
                                    post(
                                      "leases/delete",
                                      { server, scope_id: scopeId, ip: l.ip_address },
                                      () => refresh("leases"),
                                    )
                                  }
                                  disabled={!!busy}
                                  title="Release this lease back to the pool"
                                  className="ml-3 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                          {leases && leases.length === 0 && (
                            <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No active leases.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {tab === "options" && (
                    <>
                      <div className="flex flex-wrap items-end gap-2 mb-3">
                        <select value={optId} onChange={(e) => setOptId(e.target.value)} className={INPUT} title="Option">
                          <option value="6">6 — DNS servers</option>
                          <option value="3">3 — Router / gateway</option>
                          <option value="15">15 — DNS domain</option>
                          <option value="44">44 — WINS servers</option>
                          <option value="42">42 — NTP servers</option>
                        </select>
                        <input value={optVal} onChange={(e) => setOptVal(e.target.value)} placeholder="value(s), comma-separated" className={`${INPUT} w-64`} />
                        <button
                          onClick={() =>
                            post("options", { server, scope_id: scopeId, option_id: Number(optId), values: optVal.split(",").map((v) => v.trim()).filter(Boolean) }, () => { setOptVal(""); refresh("options"); })
                          }
                          disabled={!!busy || !optVal.trim()}
                          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Set option
                        </button>
                      </div>
                      <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-lg">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                            <tr>
                              <SortableTh label="Option" sortKey="option_id" sort={optSort.sort} onSort={optSort.toggle} />
                              <SortableTh label="Name" sortKey="name" sort={optSort.sort} onSort={optSort.toggle} />
                              <th className="px-3 py-2 text-left">Value</th>
                              <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {options === null && (
                              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                            )}
                            {sortRows(options || [], optSort.sort, (o, key) =>
                              key === "name" ? (o.name || "").toLowerCase() : o.option_id,
                            ).map((o, i) => (
                              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{o.option_id}</td>
                                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{o.name}</td>
                                <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300 break-all">{(o.value || []).join(", ")}</td>
                                <td className="px-3 py-1.5 text-right">
                                  {/* Removing is NOT setting it empty: this makes the
                                      scope inherit the server value again, which is
                                      what "clear it" almost always means. An empty
                                      value hands clients an empty string. */}
                                  <button
                                    onClick={() => {
                                      if (
                                        !window.confirm(
                                          `Remove option ${o.option_id} (${o.name}) from scope ${scopeId}?\n\n` +
                                            `The scope goes back to inheriting whatever the server ` +
                                            `sets for this option. If the server sets nothing, ` +
                                            `clients get nothing.`,
                                        )
                                      )
                                        return;
                                      post(
                                        "options/delete",
                                        { server, scope_id: scopeId, option_id: o.option_id },
                                        () => refresh("options"),
                                      );
                                    }}
                                    disabled={!!busy}
                                    className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                                  >
                                    Inherit from server
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {options && options.length === 0 && (
                              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No options set on this scope — it inherits everything from the server.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {tab === "exclusions" && (
                    <>
                      <div className="flex flex-wrap items-end gap-2 mb-3">
                        <input value={exStart} onChange={(e) => setExStart(e.target.value)} placeholder="Start IP" className={`${INPUT} w-32`} />
                        <input value={exEnd} onChange={(e) => setExEnd(e.target.value)} placeholder="End IP" className={`${INPUT} w-32`} />
                        <button
                          onClick={() => post("exclusions", { server, scope_id: scopeId, start: exStart, end: exEnd }, () => { setExStart(""); setExEnd(""); refresh("exclusions"); })}
                          disabled={!!busy || !exStart.trim() || !exEnd.trim()}
                          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Add exclusion
                        </button>
                      </div>
                      <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-lg">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                            <tr>
                              <SortableTh label="Start" sortKey="start" sort={exclSort.sort} onSort={exclSort.toggle} />
                              <SortableTh label="End" sortKey="end" sort={exclSort.sort} onSort={exclSort.toggle} />
                              <th></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {exclusions === null && (
                              <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                            )}
                            {sortRows(exclusions || [], exclSort.sort, (x, key) =>
                              key === "end" ? (x.end || "").toLowerCase() : (x.start || "").toLowerCase(),
                            ).map((x, i) => (
                              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{x.start}</td>
                                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{x.end}</td>
                                <td className="px-3 py-1.5 text-right">
                                  <button
                                    onClick={() => post("exclusions/delete", { server, scope_id: scopeId, start: x.start, end: x.end }, () => refresh("exclusions"))}
                                    disabled={!!busy}
                                    className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {exclusions && exclusions.length === 0 && (
                              <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No exclusions.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
        </div>
      )}
    </div>
  );
}
